import type { IReservationTableRef } from '@/interfaces/IReservation';
import type {
  IReservationSettings,
  IReservationShift,
} from '@/interfaces/IReservationSettings';
import {
  effectiveOnlineMaxParty,
  maxBookableOnlineParty,
} from './reservation-capacity';
import type { IReservationCapacityGroup } from './reservation-capacity';
import { previewArrivalTimes } from './reservation-preview';
import type {
  IArrivalPreviewSettings,
  IArrivalPreviewTime,
} from './reservation-preview';
import { isOnlineReservationTable } from './reservation-tables';
import {
  addDays,
  defaultBoardDate,
  formatTime,
  timeToMinutes,
  toDateKey,
} from './reservation-time';

/**
 * Client-side replica of what a guest sees (D8): computed from the draft with
 * no API call, so it reflects unsaved seats and follows every answer.
 */
export interface IGuestPreviewInput {
  now: Date;
  timeZone: string;
  businessDayStartHour?: number;
  shifts: readonly Pick<
    IReservationShift,
    'name' | 'days' | 'from' | 'to' | 'arrivalTimes' | 'slotIntervalMin'
  >[];
  settings: IArrivalPreviewSettings &
    Pick<
      IReservationSettings,
      'minPartyOnline' | 'maxPartyOnline' | 'minLeadMin'
    >;
  tables: readonly IReservationTableRef[];
  groups: readonly IReservationCapacityGroup[];
  contact: {
    email: string | null;
    tel: string | null;
    whatsapp: string | null;
  };
}

export interface IGuestPreviewDay {
  date: string;
  /** Sunday = 0 through Saturday = 6, like shift days. */
  weekday: number;
  dayOfMonth: number;
  today: boolean;
  open: boolean;
}

export interface IGuestPreviewShift {
  name: string;
  /**
   * Every arrival a party can still book on the sample day (never a past or
   * too-soon one, E55); the screen shows the first six.
   */
  times: string[];
  hidden: number;
  last: string | null;
}

export interface IGuestPreviewModel {
  days: IGuestPreviewDay[];
  /**
   * The day the replica marks and lists times for: Thursday first, else the
   * first open day, among the days that still list a time; when none does,
   * among the open days (its times are then empty).
   */
  sampleDay: IGuestPreviewDay | null;
  shifts: IGuestPreviewShift[];
  /** Some open day has a shift with arrival times, bookable now or not. */
  offersTimes: boolean;
  maxParty: number;
  bookable: number;
  partyChips: number[];
  moreParties: boolean;
  onlineTables: number;
  groups: number;
  contact: { call: boolean; whatsapp: boolean; email: string | null };
  mail: { date: string; time: string } | null;
}

const PREVIEW_DAYS = 7;
const SHOWN_TIMES = 6;
const MAX_PARTY_CHIPS = 9;
const THURSDAY = 4;
const MINUTE_MS = 60_000;

function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

function hasText(value: string | null): boolean {
  return (value ?? '').trim() !== '';
}

/**
 * The first local minute («YYYY-MM-DD HH:MM», branch wall time) a guest can
 * still book online: now plus the minimum notice, the API's `lead` rule
 * (reservations-availability.util.ts: a start before it is refused, so past
 * arrivals never qualify). Rounded up to the minute because arrivals start on
 * whole minutes. Wall-clock like the rest of the replica, so a DST night may
 * differ by one chip from the engine.
 */
export function earliestBookableKey(
  now: Date,
  timeZone: string,
  minLeadMin: number
): string {
  const earliest = new Date(
    Math.ceil((now.getTime() + minLeadMin * MINUTE_MS) / MINUTE_MS) * MINUTE_MS
  );
  return `${toDateKey(earliest, timeZone)} ${formatTime(earliest, timeZone)}`;
}

/**
 * An arrival of the service `date` is listed only from the earliest bookable
 * minute on (E55: the replica, like the storefront's time grid, never lists a
 * past or too-soon time). An after-midnight arrival starts on the next date.
 */
export function isListedArrival(
  date: string,
  arrival: Pick<IArrivalPreviewTime, 'time' | 'nextDay'>,
  earliestKey: string
): boolean {
  const day = arrival.nextDay ? addDays(date, 1) : date;
  return `${day} ${arrival.time}` >= earliestKey;
}

/** Thursday first, else the first day. */
function pickSampleDay(
  candidates: readonly IGuestPreviewDay[]
): IGuestPreviewDay | null {
  return (
    candidates.find((day) => day.weekday === THURSDAY) ?? candidates[0] ?? null
  );
}

export function buildGuestPreview(
  input: IGuestPreviewInput
): IGuestPreviewModel {
  const start = defaultBoardDate(
    input.now,
    input.timeZone,
    input.businessDayStartHour ?? 4
  );
  const days: IGuestPreviewDay[] = [];
  for (let offset = 0; offset < PREVIEW_DAYS; offset++) {
    const date = addDays(start, offset);
    const weekday = weekdayOf(date);
    days.push({
      date,
      weekday,
      dayOfMonth: Number(date.slice(8, 10)),
      today: offset === 0,
      open: input.shifts.some((shift) => shift.days.includes(weekday)),
    });
  }
  // The arrivals each shift offers to some party, whatever the day.
  const offered = input.shifts.map((shift) => ({
    shift,
    arrivals: previewArrivalTimes(shift, input.settings).filter(
      (time) => time.served.length > 0
    ),
  }));
  const openDays = days.filter((day) => day.open);
  const offersTimes = offered.some(
    ({ shift, arrivals }) =>
      arrivals.length > 0 &&
      openDays.some((day) => shift.days.includes(day.weekday))
  );
  const earliestKey = earliestBookableKey(
    input.now,
    input.timeZone,
    input.settings.minLeadMin
  );
  /** A day's shifts with the times a guest can still book (E55). */
  const shiftsOf = (day: IGuestPreviewDay): IGuestPreviewShift[] =>
    offered
      .filter(({ shift }) => shift.days.includes(day.weekday))
      .map(({ shift, arrivals }) => {
        const times = arrivals
          .filter((time) => isListedArrival(day.date, time, earliestKey))
          .map((time) => time.time);
        return {
          name: shift.name,
          times,
          hidden: Math.max(0, times.length - SHOWN_TIMES),
          last: times.length ? times[times.length - 1] : null,
        };
      });
  const shiftsByDate = new Map(
    openDays.map((day) => [day.date, shiftsOf(day)] as const)
  );
  const listsTimes = (day: IGuestPreviewDay) =>
    (shiftsByDate.get(day.date) ?? []).some((shift) => shift.times.length > 0);
  const sampleDay =
    pickSampleDay(openDays.filter(listsTimes)) ?? pickSampleDay(openDays);
  const shifts: IGuestPreviewShift[] = sampleDay
    ? shiftsByDate.get(sampleDay.date) ?? []
    : [];
  const bookable = maxBookableOnlineParty(input.tables, input.groups);
  const maxParty = effectiveOnlineMaxParty(
    input.settings.minPartyOnline,
    input.settings.maxPartyOnline,
    bookable
  );
  const chips = Math.min(MAX_PARTY_CHIPS, Math.max(maxParty, 1));
  const partyChips: number[] = [];
  for (let party = 1; party <= chips; party++) partyChips.push(party);
  const firstTime = shifts.find((shift) => shift.times.length > 0)?.times[0];
  return {
    days,
    sampleDay,
    shifts,
    offersTimes,
    maxParty,
    bookable,
    partyChips,
    moreParties: maxParty > MAX_PARTY_CHIPS,
    onlineTables: input.tables.filter(
      (table) => isOnlineReservationTable(table) && table.capacity > 0
    ).length,
    groups: input.groups.length,
    contact: {
      call: hasText(input.contact.tel),
      whatsapp: hasText(input.contact.whatsapp),
      email: input.contact.email,
    },
    mail:
      sampleDay && firstTime !== undefined
        ? { date: sampleDay.date, time: firstTime }
        : null,
  };
}

export type ISlotPeriod = 'midday' | 'afternoon' | 'night';

const MIDDAY_FROM_MIN = 6 * 60;
const AFTERNOON_FROM_MIN = 16 * 60;
const NIGHT_FROM_MIN = 20 * 60;

/** Part of the day of an arrival «HH:MM» (branch wall time): Mediodía 06:00–15:59, Tarde 16:00–19:59, Noche 20:00–05:59. */
export function slotPeriod(time: string): ISlotPeriod {
  const minutes = timeToMinutes(time);
  if (minutes < MIDDAY_FROM_MIN || minutes >= NIGHT_FROM_MIN) return 'night';
  return minutes < AFTERNOON_FROM_MIN ? 'midday' : 'afternoon';
}

export interface IGuestTimeGroup {
  /** The owner's shift name; null for a period group and for shifts without a name. */
  shift: string | null;
  /** Set only when no shift of the day has a name. */
  period: ISlotPeriod | null;
  /** False when the day has a single group: the storefront then shows no heading. */
  showHeading: boolean;
  /** In order of appearance, each time once. */
  times: string[];
}

/**
 * The sample day's times grouped as the storefront front door groups slots:
 * by the owner's shift name when any shift with times has one, otherwise by
 * part of the day. Arrivals are read in model order (shift by shift, each
 * from its start, after-midnight arrivals last); groups keep the order of
 * their first arrival.
 */
export function groupGuestTimes(
  shifts: readonly Pick<IGuestPreviewShift, 'name' | 'times'>[]
): IGuestTimeGroup[] {
  const arrivals: { time: string; shift: string | null }[] = [];
  for (const shift of shifts)
    for (const time of shift.times)
      arrivals.push({ time, shift: shift.name.trim() || null });
  const byShift = arrivals.some((arrival) => arrival.shift !== null);
  const groups: Omit<IGuestTimeGroup, 'showHeading'>[] = [];
  for (const arrival of arrivals) {
    const shift = byShift ? arrival.shift : null;
    const period = byShift ? null : slotPeriod(arrival.time);
    let group = groups.find(
      (entry) => entry.shift === shift && entry.period === period
    );
    if (!group) {
      group = { shift, period, times: [] };
      groups.push(group);
    }
    if (!group.times.includes(arrival.time)) group.times.push(arrival.time);
  }
  return groups.map((group) => ({
    ...group,
    showHeading:
      groups.length > 1 && (group.shift !== null || group.period !== null),
  }));
}
